import { Button, Checkbox, Col, Divider, Flex, Form, Input, Row, Tooltip, Typography } from "antd";
import { CopyableButton } from "@vyne-shared/components/Copyable";
import { FormItemGroup, FormSwitchGroup } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import { InfoCircleOutlined } from "@ant-design/icons";
import { generateBillPayLink } from "./generateBillPayLink";
import { useSetProviderSettingsPracticeBillPay } from "api/clients/useSetProviderSettingsPracticeBillPay";
import img_make_a_payment from "/make_a_payment.png";
import type { ProviderSettingsPractice } from "utils/providerSettings";

const { Title } = Typography;

export const ProviderWritebackEnabled = () => (
    <FormSwitchGroup
        switchProps={{ name: "autoWritebackEnabled", label: "Enable/Disable PMS Writeback", required: true }}
        desc="Enable automated Payments Writeback to seamlessly update patient ledgers in a balance-forward manner after a
            patient makes a payment."
    />
);

export const ProviderNotificationItems = () => {
    const form = Form.useFormInstance<ProviderSettingsPractice>();

    const notificationEach = Form.useWatch("notificationEach", form);
    const notificationDaily = Form.useWatch("notificationDaily", form);
    const notificationWeekly = Form.useWatch("notificationWeekly", form);

    const notificationEmailEnabled = notificationEach || notificationDaily || notificationWeekly;

    return (
        <FormItemGroup legendTitle="Payment Notifications" className="payment-notifications">
            <Form.Item name="notificationEach" valuePropName="checked">
                <Checkbox>Get notified for every payment</Checkbox>
            </Form.Item>
            <Form.Item name="notificationDaily" valuePropName="checked">
                <Checkbox>Receive a daily summary of all payments</Checkbox>
            </Form.Item>
            <Form.Item name="notificationWeekly" valuePropName="checked">
                <Checkbox>Receive a weekly summary of all payments</Checkbox>
            </Form.Item>
            <Form.Item
                hidden={!notificationEmailEnabled}
                label="Email"
                name="notificationEmail"
                required={notificationEmailEnabled}
                rules={[
                    {
                        required: notificationEmailEnabled,
                        message: "Email address is required to send notifications",
                        pattern: /(^.+[@].+$|^$)/, // something@example OR nothing at all
                    },
                ]}
                shouldUpdate
            >
                <Input type="email" disabled={!notificationEmailEnabled} required={notificationEmailEnabled} />
            </Form.Item>
        </FormItemGroup>
    );
};

interface WebsiteBillPayProps {
    paymentUrl: string;
}

export const WebsiteBillPay = ({ paymentUrl }: WebsiteBillPayProps) => {
    const { mutate, isPending } = useSetProviderSettingsPracticeBillPay();

    return (
        <FormItemGroup legendTitle="Website Bill Pay" className="website-bill-pay">
            <p>
                Enable Practice Bill Pay on your website to give patients a convenient way to pay online after receiving
                a payment request.
            </p>
            {paymentUrl ? (
                <WebsiteBillPayDetails paymentUrl={paymentUrl} />
            ) : (
                <Button className="generate-bill-pay-btn" type="primary" onClick={() => mutate()} loading={isPending}>
                    Generate Payment URL
                </Button>
            )}
        </FormItemGroup>
    );
};

const WebsiteBillPayDetails = ({ paymentUrl }: WebsiteBillPayProps) => (
    <Row className="website-bill-pay-content">
        <Col span={11} className="website-bill-pay-link-wrapper">
            <div>
                <Title level={4}>Online Payment URL</Title>
                <p>Copy the link below and paste it into your website.</p>
                <Button type="link" href={paymentUrl} target="_blank">
                    {paymentUrl}
                </Button>
            </div>
            <CopyableButton className="bill-pay-copy-link" copyText={paymentUrl}>
                Copy Link
            </CopyableButton>
        </Col>
        <Divider type="vertical" />
        <Col span={13} className="website-bill-pay-embed-wrapper">
            <Flex>
                <Flex vertical={true} justify="space-between">
                    <div>
                        <Title level={4}>
                            Website Payment Button{" "}
                            <Tooltip
                                title="Provide this code to your web designer to add a payment button to your website."
                                placement="right"
                            >
                                <InfoCircleOutlined />
                            </Tooltip>
                        </Title>
                        <p>Copy the code to put a "Make a Payment" button on your website.</p>
                    </div>
                    <CopyableButton className="bill-pay-copy-link" copyText={generateBillPayLink(paymentUrl)}>
                        Copy Embed Code
                    </CopyableButton>
                </Flex>
                <Flex justify="center" align="center" className="website-bill-pay-btn-wrapper">
                    <a href={paymentUrl} target="_blank">
                        <img src={img_make_a_payment} alt="Make a Payment button" />
                    </a>
                </Flex>
            </Flex>
        </Col>
    </Row>
);
